/**
 * Resolución unificada de contenido BaseTemplate:
 * Worker KV (siteSettings inyectado) → GET /api/site-settings → props de página.
 * Cloudinary: public_id → URL + transforms (hero, logo, galería).
 */

import type { SiteSettings } from "@/hooks/useSiteSettings";
import { BRAND_BANNER_IMAGE_URL, BRAND_NAME, BRAND_AGE } from "@/lib/brandConfig";
import { SITE_SETTINGS_CLIENT_DEFAULTS } from "@/lib/siteSettingsDefaults";
import { brandDisplayNameFromSiteTitle } from "@/lib/siteBrandFromSettings";
import {
  getCloudinaryHero,
  getCloudinarySrcSet,
  getCloudinaryUrl,
  isCloudinaryImage,
  isSameCloudinaryImage,
  resolveLogoUrl,
} from "@/lib/cloudinaryUtils";
import { getServerTemplateConfig } from "@/lib/serverTemplateDetection";

export type BaseTemplateSiteProps = {
  headerUrl?: string;
  logoUrl?: string;
  heroTitle?: string;
  heroDescription?: string;
  bannerImageUrl?: string;
  footerTagline?: string;
  brandName?: string;
  showGallerySection?: boolean;
  showTallySection?: boolean;
  showSobreMi?: boolean;
  sobreMi?: string;
  bannerDeferred?: boolean;
  galleryDeferred?: boolean;
};

export type BaseTemplateGallerySettings = Pick<
  SiteSettings,
  | "galleryImageUrls"
  | "alternativeGalleryEnabled"
  | "alternativeGalleryUrls"
  | "logoUrl"
>;

export type ResolvedBaseTemplateSiteContent = {
  /** Primera pintada desde Worker KV (__TEMPLATE_CONFIG__.siteSettings). */
  hydratedFromWorker: boolean;
  /** Fetch inicial a /api/site-settings aún en curso (sin hidratación KV). */
  isSiteContentPending: boolean;
  headerUrl: string;
  brandName: string;
  logoUrl?: string;
  logoDisplaySrc?: string;
  heroTitle: string;
  heroDescription: string;
  sobreMi?: string;
  footerTagline?: string;
  bannerRaw?: string;
  bannerDisplaySrc?: string;
  bannerSrcSet?: string;
  showGallerySection: boolean;
  showTallySection: boolean;
  showSobreMi: boolean;
  gallerySettings: BaseTemplateGallerySettings;
  bannerDeferred: boolean;
  galleryDeferred: boolean;
  /** Edad de la artista desde site-settings o fallback BRAND_AGE. Vacío si no hay. */
  brandAge?: string;
};

function trimUrl(value?: string): string | undefined {
  const v = value?.trim();
  return v || undefined;
}

export function isSiteSettingsHydratedFromWorker(): boolean {
  return !!getServerTemplateConfig()?.siteSettings;
}

/** Primera pintada con Worker: siteSettings (incluye heroCta unificado + brandAge) en __TEMPLATE_CONFIG__. */
export function isWorkerSiteContentHydrated(): boolean {
  const cfg = getServerTemplateConfig();
  return !!(cfg?.siteSettings || cfg?.heroCta?.enabled);
}

function urlsWithoutLogo(urls: string[], logoUrl?: string): string[] {
  const trimmed = urls
    .filter((u): u is string => typeof u === "string" && u.trim().length > 0)
    .map((u) => u.trim());
  if (!logoUrl) return trimmed;
  return trimmed.filter((u) => !isSameCloudinaryImage(logoUrl, u));
}

function cloudinaryOnly(urls: string[], logoUrl?: string): string[] {
  return urlsWithoutLogo(urls, logoUrl).filter((u) => isCloudinaryImage(u));
}

/**
 * Banner hero — misma prioridad que Index.tsx:
 * bannerImageUrl admin → galería alternativa[0] → galleryImageUrls[0] (sin logo).
 */
export function resolveBannerFromSiteSettings(
  settings: SiteSettings,
  propBanner?: string,
): string | undefined {
  const explicit = trimUrl(propBanner) ?? trimUrl(settings.bannerImageUrl);
  if (explicit) return explicit;

  const logoUrl = trimUrl(settings.logoUrl);

  if (settings.alternativeGalleryEnabled === true) {
    const alt = urlsWithoutLogo(settings.alternativeGalleryUrls ?? [], logoUrl);
    if (alt[0]) return alt[0];
  }

  const gallery = cloudinaryOnly(settings.galleryImageUrls ?? [], logoUrl);
  if (gallery[0]) return gallery[0];

  const brandBanner = trimUrl(BRAND_BANNER_IMAGE_URL) ?? trimUrl(SITE_SETTINGS_CLIENT_DEFAULTS.bannerImageUrl);
  return brandBanner;
}

export function resolveHeaderLinkFromSiteSettings(
  settings: SiteSettings,
  propHeader?: string,
): string {
  const raw = trimUrl(propHeader) ?? trimUrl(settings.headerUrl) ?? "/";
  if (raw.startsWith("/") || raw.startsWith("http")) return raw;
  return "/";
}

export function resolveBrandNameFromSiteSettings(
  settings: SiteSettings,
  propBrand?: string,
  fallback = BRAND_NAME,
): string {
  if (propBrand && propBrand !== BRAND_NAME) return propBrand;
  return brandDisplayNameFromSiteTitle(settings.title) || fallback;
}

export function toCloudinaryBannerDelivery(
  raw: string | undefined,
  maxWidth = 1920,
): { src?: string; srcSet?: string } {
  if (!raw) return {};
  const resolved = resolveLogoUrl(raw) ?? raw;
  if (!resolved.includes("res.cloudinary.com")) {
    return { src: resolved };
  }
  const src = getCloudinaryHero(resolved, maxWidth);
  const srcSet = getCloudinarySrcSet(resolved, [800, 1200, 1600, 1920], {
    fit: "limit",
    watermark: false,
  });
  return { src, srcSet };
}

export function toCloudinaryLogoDelivery(
  raw: string | undefined,
  size = 64,
): string | undefined {
  if (!raw) return undefined;
  const resolved = resolveLogoUrl(raw) ?? raw;
  if (resolved.includes("res.cloudinary.com")) {
    return getCloudinaryUrl(resolved, {
      width: size,
      height: size,
      fit: "fill",
      watermark: false,
    });
  }
  return resolved;
}

export function resolveBaseTemplateSiteContent(
  settings: SiteSettings,
  isLoading: boolean,
  props: BaseTemplateSiteProps,
): ResolvedBaseTemplateSiteContent {
  const hydratedFromWorker = isSiteSettingsHydratedFromWorker();
  const pendingFromFetch = isLoading && !hydratedFromWorker;
  const logoUrl = trimUrl(props.logoUrl) ?? trimUrl(settings.logoUrl);
  const bannerRaw = resolveBannerFromSiteSettings(settings, props.bannerImageUrl);
  const bannerDeferred = props.bannerDeferred ?? pendingFromFetch;
  const galleryDeferred = props.galleryDeferred ?? pendingFromFetch;
  const bannerDelivery = bannerDeferred
    ? {}
    : toCloudinaryBannerDelivery(bannerRaw);

  return {
    hydratedFromWorker,
    isSiteContentPending: pendingFromFetch,
    headerUrl: resolveHeaderLinkFromSiteSettings(settings, props.headerUrl),
    brandName: resolveBrandNameFromSiteSettings(settings, props.brandName),
    logoUrl,
    logoDisplaySrc: toCloudinaryLogoDelivery(logoUrl),
    heroTitle:
      trimUrl(props.heroTitle) ??
      trimUrl(settings.title) ??
      SITE_SETTINGS_CLIENT_DEFAULTS.title,
    heroDescription:
      trimUrl(props.heroDescription) ??
      trimUrl(settings.description) ??
      SITE_SETTINGS_CLIENT_DEFAULTS.description,
    sobreMi: props.sobreMi ?? settings.sobreMi,
    footerTagline: trimUrl(props.footerTagline) ?? trimUrl(settings.footerTagline),
    bannerRaw,
    bannerDisplaySrc: bannerDelivery.src,
    bannerSrcSet: bannerDelivery.srcSet,
    showGallerySection: props.showGallerySection ?? settings.showGallerySection ?? true,
    showTallySection: props.showTallySection ?? settings.showTallySection ?? true,
    showSobreMi: props.showSobreMi ?? settings.showSobreMi !== false,
    gallerySettings: {
      galleryImageUrls: settings.galleryImageUrls,
      alternativeGalleryEnabled: settings.alternativeGalleryEnabled,
      alternativeGalleryUrls: settings.alternativeGalleryUrls,
      logoUrl,
    },
    bannerDeferred,
    galleryDeferred,
    // brandAge (y heroCta) vienen del Worker (inyección unificada dentro de __TEMPLATE_CONFIG__.siteSettings).
    // Worker + detailed geo = fuente coherente para first-paint (edad solo footer, heroCta admin override).
    brandAge: (settings.brandAge?.trim() || BRAND_AGE || "").trim() || undefined,
  };
}